import {
  BUSINESS,
  SERVICES,
  FAQ_ITEMS,
  PROCESS_DETAIL_STEPS,
  FOUNDER,
} from "./constants";
import { OG_IMAGE } from "./images";

const SITE_URL = "https://bluerockconcreteco.com";

/** LocalBusiness / GeneralContractor schema — rendered in __root head(). */
export function getLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "@id": `${SITE_URL}/#business`,
    name: BUSINESS.name,
    image: OG_IMAGE,
    url: SITE_URL,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    logo: `${SITE_URL}/blue-rock-logo-flame.png`,
    description:
      "Family owned concrete contractor in Wilmington, Delaware. Driveways, patios, stamped and decorative concrete, steps, retaining walls, repair, and epoxy floors across Delaware, Pennsylvania, and Maryland.",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Wilmington",
      addressRegion: "DE",
      addressCountry: "US",
    },
    areaServed: BUSINESS.serviceAreas.map((a) => ({ "@type": "State", name: a })),
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        opens: "07:00",
        closes: "19:00",
      },
    ],
    // NOTE: `aggregateRating` is deliberately absent until the Google Business
    // Profile is live and BUSINESS.rating / BUSINESS.reviews hold real numbers.
    // The 4.9 / 60 currently in constants.ts are placeholders, and emitting
    // review structured data that no real reviews back is a Google
    // structured-data policy violation that risks a manual action. Restore this
    // block, sourced from the real GBP figures, at launch.
    //
    // `sameAs` is likewise omitted: BUSINESS.social still points at bare
    // facebook.com / instagram.com / google.com/maps URLs. A sameAs full of
    // stubs is worse than no sameAs at all. Add it once the real profiles exist.
    foundingDate: String(BUSINESS.founded),
    knowsAbout: [
      "Concrete driveways",
      "Stamped concrete",
      "Concrete patios",
      "Concrete repair",
      "Epoxy garage floors",
    ],
    makesOffer: SERVICES.map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s.title },
    })),
  };
}

export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BUSINESS.name,
    url: SITE_URL,
  };
}

/** ItemList of all services — rendered in the /services route head(). */
export function getServicesPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Concrete Services",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Service",
        name: s.title,
        description: s.blurb,
        url: `${SITE_URL}/services#${s.slug}`,
        provider: { "@id": `${SITE_URL}/#business` },
      },
    })),
  };
}

export function getFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/** HowTo schema of the 7 build steps — rendered in the /process head(). */
export function getProcessPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Blue Rock Concrete builds your project",
    description:
      "Our 7 step process from free on-site estimate to sealed, warrantied concrete.",
    step: PROCESS_DETAIL_STEPS.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.points.map((p) => `${p.heading}: ${p.body}`).join(" "),
    })),
  };
}

/**
 * AboutPage + the owner as a Person, and the sister company as a related
 * organization. Person markup on an About page is the standard way to attach a
 * real named human to the business, which matters for E-E-A-T: the site claims
 * "owner on every job" and this is the machine-readable version of that claim.
 */
export function getAboutPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    url: `${SITE_URL}/about`,
    name: `About ${BUSINESS.name}`,
    mainEntity: {
      "@type": "LocalBusiness",
      name: BUSINESS.name,
      url: SITE_URL,
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      foundingDate: String(BUSINESS.founded),
      areaServed: BUSINESS.serviceAreas.map((a) => ({
        "@type": "AdministrativeArea",
        name: a,
      })),
      founder: {
        "@type": "Person",
        name: FOUNDER.name,
        jobTitle: FOUNDER.role,
        worksFor: { "@type": "Organization", name: BUSINESS.name },
      },
      subOrganization: {
        "@type": "Organization",
        name: BUSINESS.sisterCompany,
        url: BUSINESS.sisterUrl,
      },
    },
  };
}
